import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { BRAND, CONTACT, SOCIAL } from '@/constants/site';
import { FOOTER } from '@/constants/testIds';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.78a8.16 8.16 0 0 0 4.77 1.52V7.85a4.85 4.85 0 0 1-1.84-.16z" />
  </svg>
);

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] overflow-hidden bb-grain"
    >
      <div className="relative z-10 bb-container py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">{BRAND.legalName}</div>
            <h3 className="bb-display text-4xl md:text-5xl mt-4 leading-[0.95]">
              Bombay <span className="bb-italic text-[hsl(var(--bb-gold))]">Bespoke</span>
              <br />
              Fashion House
            </h3>
            <p className="mt-5 text-[hsl(var(--bb-text))]/70 text-sm leading-relaxed max-w-md">
              Thirty years of craftsmanship. A house of two — Mr. Hardeep Singh Gill and
              Mrs. Vandana — pairing menswear precision with women’s couture, from Dubai
              to the world.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Visit</div>
            <ul className="mt-5 space-y-3 text-sm text-[hsl(var(--bb-text))]/85">
              <li className="flex items-start gap-3" data-testid={FOOTER.address}>
                <MapPin size={15} className="mt-1 text-[hsl(var(--bb-gold))] shrink-0" />
                <span>
                  {CONTACT.addressLine1}
                  <br />
                  {CONTACT.addressLine2}
                </span>
              </li>
              <li className="flex items-center gap-3" data-testid={FOOTER.phone}>
                <Phone size={14} className="text-[hsl(var(--bb-gold))]" />
                <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-[hsl(var(--bb-gold))] transition-colors">
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-center gap-3" data-testid={FOOTER.email}>
                <Mail size={14} className="text-[hsl(var(--bb-gold))]" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-[hsl(var(--bb-gold))] transition-colors">
                  {CONTACT.email}
                </a>
              </li>
            </ul>
            <div className="mt-5 text-xs text-[hsl(var(--bb-text))]/55">
              {CONTACT.hours}
              <br />
              {CONTACT.hoursSunday}
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">House</div>
            <ul className="mt-5 grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <li><Link to="/womens-design" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Women’s Design</Link></li>
              <li><Link to="/mens-bespoke" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Men’s Bespoke</Link></li>
              <li><Link to="/our-story" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Our Story</Link></li>
              <li><Link to="/testimonials" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Testimonials</Link></li>
              <li><Link to="/book-appointment" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Book Appointment</Link></li>
              <li><Link to="/contact" className="hover:text-[hsl(var(--bb-gold))] transition-colors">Contact</Link></li>
            </ul>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid={FOOTER.instagram}
                className="inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--bb-gold))]/40 hover:border-[hsl(var(--bb-gold))] hover:text-[hsl(var(--bb-gold))] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={SOCIAL.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid={FOOTER.tiktok}
                className="inline-flex items-center justify-center w-10 h-10 border border-[hsl(var(--bb-gold))]/40 hover:border-[hsl(var(--bb-gold))] hover:text-[hsl(var(--bb-gold))] transition-colors"
                aria-label="TikTok"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[hsl(var(--bb-text))]/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--bb-text))]/50">
          <span>© {new Date().getFullYear()} {BRAND.legalName}</span>
          <span>Dubai — Atelier by Mr. Hardeep Singh Gill &amp; Mrs. Vandana</span>
        </div>
      </div>
    </footer>
  );
}
